import React, { useEffect, useState } from "react";
import {
  fetchUiState,
  saveUiState,
  fetchOpenRouterModels,
  type OpenRouterModel,
} from "../../api/client";
import {
  getExecutionSettings,
  saveExecutionSettings,
  type ExecutionSettings,
} from "../../api/workbench";

/* Settings — configurable model + system prompt used when generating an agent
   from a natural-language description (the Add-agent flow, PR6). Persisted
   server-side under the `agentGen` ui-state key so the generation path reads the
   same config the user sees here. */
export interface AgentGenSettings {
  model: string;
  prompt: string;
}

const DEFAULT_PROMPT = `You configure agents for an email-triage system. Given a plain-language description of the job an agent should do, return a SINGLE agent configuration as strict JSON with exactly these fields:
{
  "name": string,
  "desc": string,
  "kind": "spine" | "workstream",
  "slug": string,
  "model": string,            // OpenRouter model slug
  "tau": number,              // 0-1 confidence threshold
  "outTok": number,           // max output tokens
  "threshold_rationale": string,
  "prompt": string,           // the full SYSTEM/USER template the agent runs
  "trigger": string,
  "inputs": string[],
  "outputs": string[]
}
Return ONLY the JSON object — no markdown, no commentary.`;

export const AGENT_GEN_DEFAULTS: AgentGenSettings = {
  model: "anthropic/claude-opus-4-8",
  prompt: DEFAULT_PROMPT,
};

/* Execution settings — what the v2 executor and Review Tray discussion read:
   feature models, the model allowlist agents may run on, and the per-email cost
   cap. Saved values take effect on the next run or discussion, no redeploy. */
function ExecutionSettingsCard({ models }: { models: OpenRouterModel[] }): JSX.Element {
  const [s, setS] = useState<ExecutionSettings | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);
  const [allowInput, setAllowInput] = useState("");

  useEffect(() => {
    void getExecutionSettings().then(setS);
  }, []);
  if (!s) return <div className="tert" style={{ fontSize: 12.5 }}>Loading execution settings…</div>;

  const edit = (patch: Partial<ExecutionSettings>): void => {
    setS({ ...s, ...patch });
    setDirty(true);
    setSaved(false);
  };
  const save = async (): Promise<void> => {
    const next = await saveExecutionSettings(s);
    if (next) {
      setS(next);
      setDirty(false);
      setSaved(true);
    }
  };
  const modelName = (id: string): string => models.find((m) => m.id === id)?.name ?? id;

  return (
    <>
      <div className="lbar" style={{ marginTop: 22 }}>
        <div>
          <div className="up">Execution</div>
          <div className="tert" style={{ fontSize: 12, marginTop: 2 }}>
            Read before every email run and Review Tray discussion — changes apply to the next one.
          </div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => void save()} disabled={!dirty}>
          {saved ? "Saved" : "Save execution settings"}
        </button>
      </div>
      <div className="card" style={{ padding: 16 }}>
        <div className="cfgf" style={{ marginBottom: 14 }}>
          <label className="up">Semantic-evaluator model</label>
          <input
            className="inp"
            list="execModels"
            value={s.semanticEvaluatorModel}
            onChange={(e) => edit({ semanticEvaluatorModel: e.target.value })}
          />
          <datalist id="execModels">
            {models.map((m) => (
              <option key={m.id} value={m.id} />
            ))}
          </datalist>
          <div className="hint">
            Evaluates all of an email's semantic trigger conditions in one batched call. Also used
            for the tray's already-pending dedup check.
          </div>
        </div>
        <div className="cfgf" style={{ marginBottom: 14 }}>
          <label className="up">Review Tray discussion model</label>
          <select
            className="inp"
            value={s.reviewTrayDiscussionModel}
            onChange={(e) => edit({ reviewTrayDiscussionModel: e.target.value })}
          >
            {s.modelAllowlist.map((id) => (
              <option key={id} value={id}>
                {modelName(id)}
              </option>
            ))}
          </select>
          <div className="hint">
            Used only for advisory Review Tray discussions. Usage is uncapped and tracked
            separately from email-run usage.
          </div>
        </div>
        <div className="cfgf" style={{ marginBottom: 14 }}>
          <label className="up">Model allowlist</label>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {s.modelAllowlist.map((id) => (
              <label key={id} style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12.5 }}>
                <input
                  type="checkbox"
                  checked
                  onChange={() => edit({ modelAllowlist: s.modelAllowlist.filter((x) => x !== id) })}
                />
                <span>{modelName(id)}</span>
                <span className="mono tert" style={{ fontSize: 11 }}>{id}</span>
              </label>
            ))}
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 6, alignItems: "center" }}>
            <input
              className="inp"
              list="execModels"
              placeholder="Add a model slug (open-source welcome, e.g. moonshotai/kimi-2.6)"
              value={allowInput}
              onChange={(e) => setAllowInput(e.target.value)}
              style={{ flex: 1, fontSize: 12 }}
            />
            <button
              className="btn btn-ghost btn-sm"
              disabled={!allowInput.trim()}
              onClick={() => {
                const v = allowInput.trim();
                if (v && !s.modelAllowlist.includes(v)) edit({ modelAllowlist: [...s.modelAllowlist, v] });
                setAllowInput("");
              }}
            >
              Add
            </button>
          </div>
          <div className="hint">
            Agents may only run on these models; anything else falls back to the default with a run
            warning.
          </div>
        </div>
        <div className="cfgf" style={{ marginBottom: 14 }}>
          <label className="up">Per-email cost cap (USD)</label>
          <input
            className="inp numin"
            type="number"
            step="0.05"
            min="0.05"
            value={s.costCapUsd}
            onChange={(e) => edit({ costCapUsd: Math.max(0.05, parseFloat(e.target.value) || 0.05) })}
          />
          <div className="hint">
            An email that would exceed this pauses with a visible flag; remaining agents are skipped,
            never silently dropped.
          </div>
        </div>
        <div className="cfgf">
          <label className="up">Fan-out concurrency limit</label>
          <input
            className="inp numin"
            type="number"
            step="1"
            min="1"
            max="50"
            value={s.concurrencyLimit ?? 10}
            onChange={(e) =>
              edit({ concurrencyLimit: Math.max(1, Math.min(50, parseInt(e.target.value, 10) || 10)) })
            }
          />
          <div className="hint">
            Maximum simultaneous LLM calls per email run (fan-out phase). Higher values reduce
            wall-clock time when many agents can run in parallel. Stay below your OpenRouter
            project quota. Default: 10.
          </div>
        </div>
      </div>
    </>
  );
}

export function SettingsView(): JSX.Element {
  const [s, setS] = useState<AgentGenSettings>(AGENT_GEN_DEFAULTS);
  const [models, setModels] = useState<OpenRouterModel[]>([]);
  const [dirty, setDirty] = useState(false);
  const [savedAt, setSavedAt] = useState(false);

  useEffect(() => {
    void fetchUiState<AgentGenSettings>("agentGen").then((v) => {
      if (v) setS({ ...AGENT_GEN_DEFAULTS, ...v });
    });
    void fetchOpenRouterModels().then((m) => {
      if (m) setModels(m);
    });
  }, []);

  const edit = (patch: Partial<AgentGenSettings>): void => {
    setS((cur) => ({ ...cur, ...patch }));
    setDirty(true);
    setSavedAt(false);
  };

  const save = (): void => {
    void saveUiState("agentGen", s);
    setDirty(false);
    setSavedAt(true);
  };

  return (
    <div style={{ maxWidth: 760 }}>
      <ExecutionSettingsCard models={models} />
      <div className="lbar" style={{ marginTop: 22 }}>
        <div>
          <div className="up">Agent generation</div>
          <div className="tert" style={{ fontSize: 12, marginTop: 2 }}>
            Model and system prompt used when generating an agent from a natural-language
            description.
          </div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={!dirty}>
          {savedAt ? "Saved" : "Save settings"}
        </button>
      </div>

      <div className="card" style={{ padding: 16 }}>
        <div className="cfgf" style={{ marginBottom: 16 }}>
          <label className="up">Generation model</label>
          <input
            className="inp"
            list="agentGenModels"
            value={s.model}
            onChange={(e) => edit({ model: e.target.value })}
            placeholder="anthropic/claude-opus-4-8"
          />
          <datalist id="agentGenModels">
            {models.map((m) => (
              <option key={m.id} value={m.id} />
            ))}
          </datalist>
          <div className="hint">
            OpenRouter model slug. Defaults to Opus 4.8; switch to any model the proxy allows.
          </div>
        </div>

        <div className="cfgf">
          <label className="up">Generation system prompt</label>
          <textarea
            className="tarea"
            style={{ minHeight: 280, fontFamily: "var(--mono)", fontSize: 12 }}
            value={s.prompt}
            onChange={(e) => edit({ prompt: e.target.value })}
          />
          <div className="hint">
            Instructs the model how to turn a job description into a valid agent config. Keep the
            JSON-only contract intact so the result parses.
          </div>
        </div>
      </div>
    </div>
  );
}
